import type {
	W_FieldReport_FindAllReportTypesForCompany_VM,
	W_FieldReport_FindAllReportTypesForCompany_VM_Row as ReportTypeRow,
	W_FieldReport_FindAllReportTypesForCompany_VM_ReportTypeBlock,
} from 'ab-api/web/fieldReport/findAllReportTypesForCompany';

import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';

import type { ColumnDataSourceFM, TemplateColumnFM } from './formModel';

const makeCid = (): string => Math.random().toString(36).slice(2) + Date.now().toString(36);

export const withCidColumn = (column: Omit<TemplateColumnFM, '_cid'>): TemplateColumnFM => ({
	...column,
	_cid: makeCid(),
	dataSources: (column.dataSources ?? []).map(withCidDS),
});

export const withCidDS = (dataSource: Omit<ColumnDataSourceFM, '_cid'>): ColumnDataSourceFM => ({
	...dataSource,
	_cid: makeCid(),
});

interface GroupedDataSourcesByReportType {
	familyKey: string;
	reportTypeName: string;
	revisions: Array<{
		reportTypeId: number;
		revision: number;
		hasDataSource: boolean;
		isIgnored: boolean;
		dataSource?: ColumnDataSourceFM;
	}>;
	hasAnyDataSource: boolean;
	allRevisionsCovered: boolean;
}

/**
 * Group data sources by “family” (report type lineage) based on the family key.
 * Displayed family name = name of latest revision.
 */
export const groupDataSourcesByReportType = (
	dataSources: ColumnDataSourceFM[],
	fieldReportTypes: Nullable<W_FieldReport_FindAllReportTypesForCompany_VM>,
	excludedRevisionIds: number[]
): GroupedDataSourcesByReportType[] => {
	if (!fieldReportTypes?.reportTypes.length) {
		return [];
	}

	// Only dataSources with a field are relevant
	const dsWithField = dataSources.filter((ds) => !!ds?.field);

	// Build a map: reportTypeId -> dataSource (assume max 1 per revision)
	const dsByReportTypeId = dsWithField.reduce<Map<number, ColumnDataSourceFM>>((acc, ds) => {
		const rtId = ds.field!.reportTypeId;
		if (!acc.has(rtId)) {
			acc.set(rtId, ds);
		}
		return acc;
	}, new Map());

	// Group report types by family key
	const groups = fieldReportTypes.reportTypes.reduce<Map<string, ReportTypeRow[]>>(
		(acc, rt) => {
			const key = rt.familyKey;
			const arr = acc.get(key);

			if (arr) {
				arr.push(rt);
			} else {
				acc.set(key, [rt]);
			}

			return acc;
		}, new Map()
	);

	// Build grouped response
	const result = Array.from(groups.values()).reduce<GroupedDataSourcesByReportType[]>(
		(acc, rts) => {
			// Sort revisions ASC (lower number first)
			const sorted = [...rts].sort((a, b) => a.revision - b.revision);
			const latest = sorted[sorted.length - 1];

			const revisions = sorted.map((rt) => {
				const dataSource = dsByReportTypeId.get(rt.id);
				const isIgnored = excludedRevisionIds.includes(rt.id);

				return {
					reportTypeId: rt.id,
					revision: rt.revision,
					hasDataSource: !!dataSource,
					isIgnored,
					dataSource,
				};
			});

			const hasAnyDataSource = revisions.some((r) => r.hasDataSource);
			const allRevisionsCovered = revisions.every((r) => r.hasDataSource || r.isIgnored);

			acc.push({
				familyKey: latest.familyKey,
				reportTypeName: latest.name,
				revisions,
				hasAnyDataSource,
				allRevisionsCovered,
			});

			return acc;
		},
		[]
	);

	return result;
};

/**
 * Index report types by id and family:
 * - byId: nothing special here
 * - byFamily: key = family key, value = all report revisions DESC (latest first)
 */
export const indexReportTypes = (vm: Nullable<W_FieldReport_FindAllReportTypesForCompany_VM>) => {
	const byId = new Map<
		number,
		W_FieldReport_FindAllReportTypesForCompany_VM['reportTypes'][number]
	>();
	const byFamily = new Map<
		string,
		W_FieldReport_FindAllReportTypesForCompany_VM['reportTypes'][number][]
	>();

	(vm?.reportTypes ?? []).forEach((reportType) => {
		byId.set(reportType.id, reportType);
		const key = reportType.familyKey;
		const arr = byFamily.get(key) ?? [];
		arr.push(reportType);
		byFamily.set(key, arr);
	});

	byFamily.forEach((arr, key) => {
		byFamily.set(
			key,
			[...arr].sort((a, b) => b.revision - a.revision)
		);
	});

	return { byId, byFamily };
};

export const getBlockById = (rt: ReportTypeRow, reportBlockId: number) =>
	rt.reportTypeBlocks.find((rtb) => rtb.reportBlockId === reportBlockId)?.reportBlock ?? null;

type BlockVM = W_FieldReport_FindAllReportTypesForCompany_VM_ReportTypeBlock;

interface BlockGroups {
	upperTotal: Nullable<BlockVM>;
	primaryMain: Nullable<BlockVM>;
	primaryOther: BlockVM[];
	secondaryMain: Nullable<BlockVM>;
	secondaryOther: BlockVM[];
	lowerTotal: Nullable<BlockVM>;
}

export function getBlockGroups(reportType: ReportTypeRow): BlockGroups {
	const primaryMain = reportType.primaryBlocks?.find((rb) => rb.reportBlock.isMain) ?? null;
	const primaryOther = reportType.primaryBlocks?.filter((rb) => !rb.reportBlock.isMain) ?? [];
	const secondaryMain = reportType.secondaryBlocks?.find((rb) => rb.reportBlock.isMain) ?? null;
	const secondaryOther = reportType.secondaryBlocks?.filter((rb) => !rb.reportBlock.isMain) ?? [];

	return {
		upperTotal: reportType.upperTotalBlock ?? null,
		lowerTotal: reportType.lowerTotalBlock ?? null,
		primaryMain: primaryMain ?? null,
		primaryOther,
		secondaryMain: secondaryMain ?? null,
		secondaryOther,
	};
}

/**
 * Returns latest revision for each report type family
 */
export const getLatestReportTypes = (
	companyReportTypes: Nullable<W_FieldReport_FindAllReportTypesForCompany_VM>
) => {
	const latestPerFamilyMap = (companyReportTypes?.reportTypes ?? []).reduce<
		Map<
			string, // family key
			W_FieldReport_FindAllReportTypesForCompany_VM['reportTypes'][number] // latest revision
		>
	>((acc, rt) => {
		const key = rt.familyKey;
		const prev = acc.get(key);

		if (!prev || rt.revision > prev.revision) {
			acc.set(key, rt);
		}

		return acc;
	}, new Map());

	return Array.from(latestPerFamilyMap.values());
};

export const resolveFieldName = (
	companyReportTypes: Nullable<W_FieldReport_FindAllReportTypesForCompany_VM>,
	dataSource: ColumnDataSourceFM
) => {
	const field = dataSource.field;
	if (!field) {
		return null;
	}
	const reportType = companyReportTypes?.reportTypes.find((rt) => rt.id === field.reportTypeId);
	const reportBlock = reportType?.reportTypeBlocks.find(
		(rtb) => rtb.reportBlock.id === field.reportBlockId
	)?.reportBlock;

	const reportBlockField = reportBlock?.fields.find(
		(rbf) => rbf.id === field.reportBlockFieldId
	);

	if (!reportType || !reportBlock || !reportBlockField) {
		return null;
	}
	return {
		reportTypeName: reportType.name,
		reportBlockName: reportBlock.name,
		reportBlockFieldName: reportBlockField.name,
	};
};

export const findMatchingFieldInRevision = (
	reportType: W_FieldReport_FindAllReportTypesForCompany_VM['reportTypes'][number],
	targetBlockUniqueId: string,
	targetFieldName: string,
	targetUnit: Nullable<QuantityUnitType>
) => {
	// find block with same uniqueId
	const block = reportType.reportTypeBlocks.find(
		(b) => b.reportBlock.uniqueId === targetBlockUniqueId
	)?.reportBlock;

	if (!block) {
		return null;
	}

	const field = block.fields.find((f) => f.name === targetFieldName && f.unit === targetUnit);

	return field
		? { reportBlockId: block.id, reportBlockFieldId: field.id }
		: null;
};

export const buildFieldLabel = (
	companyReportTypes: Nullable<W_FieldReport_FindAllReportTypesForCompany_VM>,
	dataSource: ColumnDataSourceFM
) => {
	const meta = resolveFieldName(companyReportTypes, dataSource);
	return meta
		? `${meta.reportTypeName} - ${meta.reportBlockName} - ${meta.reportBlockFieldName}`
		: null;
};
